import React from 'react';

/**
 * Componente de paginación con selector de items por página.
 * Usa sistema adaptativo: muestra todos los números si hay ≤7 páginas,
 * o ventana deslizante con ellipsis si hay más.
 *
 * @param {Object} props - Propiedades del componente
 * @param {number} props.currentPage - Página actual (1-indexed)
 * @param {number} [props.totalPages] - Total de páginas. Si no se proporciona, se calcula desde totalItems/itemsPerPage
 * @param {number} props.itemsPerPage - Items por página (también usado en selector)
 * @param {number} props.totalItems - Total de items en el dataset
 * @param {Function} props.onPageChange - Callback al cambiar página, recibe (pageNumber: number)
 * @param {Function} [props.onItemsPerPageChange] - Callback al cambiar items por página, recibe (newLimit: number)
 * @param {string} [props.itemName='elementos'] - Nombre para mostrar en "de X {itemName}"
 * @param {number} [props.indexOfFirstItem] - Índice del primer item mostrado. Si no se proporciona, se calcula
 * @param {number} [props.indexOfLastItem] - Índice del último item mostrado. Si no se proporciona, se calcula
 * @param {boolean} [props.loading=false] - Si true, deshabilita controles y muestra feedback visual
 * @returns {JSX.Element|null} Null si no hay páginas
 */
const Pagination = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange = () => {},
  itemName = "elementos",
  indexOfFirstItem,
  indexOfLastItem,
  loading = false
}) => {
  // Calcular totalPages si no se proporciona (usando misma fórmula que backend)
  const calculatedTotalPages = totalPages || Math.ceil(totalItems / itemsPerPage) || 0;

  // Si no hay páginas, no mostrar nada
  if (calculatedTotalPages === 0) return null;

  // Calcular índices si no se proporcionan
  const firstItem = indexOfFirstItem || ((currentPage - 1) * itemsPerPage + 1);
  const lastItem = indexOfLastItem || Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={`pagination-container ${loading ? 'pagination-loading' : ''}`.trim()}>
      <div className="pagination-info">
        <div className="per-page-selector">
          <label>Mostrar:</label>
          <select value={itemsPerPage} onChange={(e) => onItemsPerPageChange(Number(e.target.value))} disabled={loading}>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
        <span>
          Mostrando {firstItem} - {lastItem} de {totalItems} {itemName}
        </span>
      </div>

      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          <i className="fas fa-chevron-left"></i>
          <span className="hide-mobile">Anterior</span>
        </button>

        <div className="pagination-numbers">
          {/* Sistema de paginación adaptativo */}
          {calculatedTotalPages <= 7 ? (
            // Si hay 7 páginas o menos, mostrar todas
            [...Array(calculatedTotalPages)].map((_, index) => {
              const pageNumber = index + 1;
              return (
                <button
                  key={pageNumber}
                  className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                  onClick={() => onPageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })
          ) : (
            // Si hay más de 7 páginas, usar ventana deslizante
            <>
              {/* Primera página */}
              <button
                className={`pagination-number ${currentPage === 1 ? 'active' : ''}`}
                onClick={() => onPageChange(1)}
              >
                1
              </button>

              {/* Dots o números del lado izquierdo */}
              {currentPage > 4 ? (
                <span className="pagination-dots">...</span>
              ) : (
                <button
                  className={`pagination-number ${currentPage === 2 ? 'active' : ''}`}
                  onClick={() => onPageChange(2)}
                >
                  2
                </button>
              )}

              {/* Números centrales - ventana de 3 números */}
              {currentPage > 4 && currentPage < calculatedTotalPages - 3 ? (
                // En el medio: mostrar current-1, current, current+1
                <>
                  <button
                    className="pagination-number"
                    onClick={() => onPageChange(currentPage - 1)}
                  >
                    {currentPage - 1}
                  </button>
                  <button
                    className="pagination-number active"
                    onClick={() => onPageChange(currentPage)}
                  >
                    {currentPage}
                  </button>
                  <button
                    className="pagination-number"
                    onClick={() => onPageChange(currentPage + 1)}
                  >
                    {currentPage + 1}
                  </button>
                </>
              ) : currentPage <= 4 ? (
                // Al inicio: mostrar 3, 4, 5
                <>
                  <button
                    className={`pagination-number ${currentPage === 3 ? 'active' : ''}`}
                    onClick={() => onPageChange(3)}
                  >
                    3
                  </button>
                  <button
                    className={`pagination-number ${currentPage === 4 ? 'active' : ''}`}
                    onClick={() => onPageChange(4)}
                  >
                    4
                  </button>
                  <button
                    className="pagination-number"
                    onClick={() => onPageChange(5)}
                  >
                    5
                  </button>
                </>
              ) : (
                // Al final: mostrar últimos 3 del medio
                <>
                  <button
                    className="pagination-number"
                    onClick={() => onPageChange(calculatedTotalPages - 4)}
                  >
                    {calculatedTotalPages - 4}
                  </button>
                  <button
                    className={`pagination-number ${currentPage === calculatedTotalPages - 3 ? 'active' : ''}`}
                    onClick={() => onPageChange(calculatedTotalPages - 3)}
                  >
                    {calculatedTotalPages - 3}
                  </button>
                  <button
                    className={`pagination-number ${currentPage === calculatedTotalPages - 2 ? 'active' : ''}`}
                    onClick={() => onPageChange(calculatedTotalPages - 2)}
                  >
                    {calculatedTotalPages - 2}
                  </button>
                </>
              )}

              {/* Dots o números del lado derecho */}
              {currentPage < calculatedTotalPages - 3 ? (
                <span className="pagination-dots">...</span>
              ) : (
                <button
                  className={`pagination-number ${currentPage === calculatedTotalPages - 1 ? 'active' : ''}`}
                  onClick={() => onPageChange(calculatedTotalPages - 1)}
                >
                  {calculatedTotalPages - 1}
                </button>
              )}

              {/* Última página */}
              <button
                className={`pagination-number ${currentPage === calculatedTotalPages ? 'active' : ''}`}
                onClick={() => onPageChange(calculatedTotalPages)}
              >
                {calculatedTotalPages}
              </button>
            </>
          )}
        </div>

        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === calculatedTotalPages || loading}
        >
          <span className="hide-mobile">Siguiente</span>
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  );
};

export default Pagination;